import React from "react";
import Modal from "@cloudscape-design/components/modal";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import Container from "@cloudscape-design/components/container";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import Header from "@cloudscape-design/components/header";
import Grid from "@cloudscape-design/components/grid";
import DatePicker from "@cloudscape-design/components/date-picker";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { walletPromotionEditModalStore } from "../stores/walletPromotionEditModal";
import { PARTNER_PAYMENT_TYPE_OPTIONS } from "../helpers/cohortConditions";
import { RISK_MODEL_OPTIONS, getRiskModelSelectedOptions } from "../helpers/riskModelOptions";

const getDateRangeI18nStrings = () => ({
	todayAriaLabel: "Today",
	nextMonthAriaLabel: "Next month",
	previousMonthAriaLabel: "Previous month",
	customRelativeRangeDurationLabel: "Duration",
	customRelativeRangeDurationPlaceholder: "Enter duration",
	customRelativeRangeOptionLabel: "Custom range",
	customRelativeRangeOptionDescription: "Define a custom range in the past",
	customRelativeRangeUnitLabel: "Unit of time",
	formatRelativeRange: ({ startDate, endDate }) => {
		const start = new Date(startDate);
		const end = new Date(endDate);
		const locale = "en-GB";
		const dateOptions = { year: "numeric", month: "long", day: "numeric" };
		const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

		const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
		const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

		return `${formattedStart} - ${formattedEnd}`;
	},
	formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
	dateTimeConstraintText: "Date and time must be within the selected range.",
	renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
	labelRelativeRange: "Relative range",
	labelAbsoluteRange: "Absolute range",
	dateTimePickerAriaLabel: "Date and time picker",
	dateTimePickerLabel: "Date and Time",
	clearButtonAriaLabel: "Clear date range",
	relativeModeTitle: "Relative mode",
	absoluteModeTitle: "Absolute mode",
	cancelButtonLabel: "Cancel",
	applyButtonLabel: "Apply",
});

class WalletPromotionEditModal extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			cohortGroups: [],
			newConditionByGroupId: {},
		};
	}

	componentDidMount() {
		this.initModal();
	}

	safeJsonParse = (value, fallback = []) => {
		try {
			if (typeof value === "string") return JSON.parse(value);
			return value || fallback;
		} catch {
			return fallback;
		}
	};

	getGroupKeyByModule = (module) => {
		if (module === "deposit") return "depositFilters";
		if (module === "withdrawal") return "withdrawalFilters";
		if (module === "registration") return "registrationFilters";
		return "profileFilters";
	};

	getCohortModuleOptions = () => [
		{ value: "deposit", label: "Deposit" },
		{ value: "withdrawal", label: "Withdrawal" },
		{ value: "registration", label: "Registration" },
		{ value: "profile", label: "Profile" },
	];

	getLogicalOperatorOptions = () => [
		{ value: "AND", label: "AND" },
		{ value: "OR", label: "OR" },
	];

	getCohortFieldOptions = () => [
		{ value: "id", label: "Id", type: "string" },
		{ value: "risk_model", label: "Risk model", type: "multiselect", specialType: "riskModel" },
		{ value: "verify", label: "Verify", type: "select", specialType: "boolean" },
		{ value: "sweepstakes_count", label: "Sweepstakes count", type: "count" },
		{ value: "tasks_count", label: "Tasks count", type: "count" },
		{ value: "email", label: "Email", type: "select", specialType: "boolean" },
		{ value: "phone", label: "Phone", type: "select", specialType: "boolean" },
		{ value: "tg", label: "Telegram", type: "select", specialType: "boolean" },
		{ value: "partnerName", label: "Partner Name", type: "string" },
		{ value: "paymentType", label: "Payment Type", type: "select", specialType: "partnerPaymentType" },
		{ value: "trafficType", label: "Traffic Type", type: "string" },
		{ value: "deposit_date", label: "Deposit date", type: "count" },
		{ value: "deposit_date_crypto", label: "Deposit crypto date", type: "count" },
		{ value: "deposit_date_fiat", label: "Deposit fiat date", type: "count" },
		{ value: "deposit_count", label: "Deposit count", type: "count" },
		{ value: "deposit_count_fiat", label: "Deposit fiat count", type: "count" },
		{ value: "deposit_count_crypto", label: "Deposit crypto count", type: "count" },
		{ value: "deposit_from", label: "Deposit from", type: "date" },
		{ value: "deposit_to", label: "Deposit to", type: "date" },
		{ value: "deposit_crypto_from", label: "Deposit crypto from", type: "date" },
		{ value: "deposit_crypto_to", label: "Deposit crypto to", type: "date" },
		{ value: "deposit_fiat_from", label: "Deposit fiat from", type: "date" },
		{ value: "deposit_fiat_to", label: "Deposit fiat to", type: "date" },
		{ value: "deposit_amount_to", label: "Deposit amount to", type: "count" },
		{ value: "deposit_amount_from", label: "Deposit amount from", type: "count" },
		{ value: "deposit_amount_crypto_to", label: "Deposit amount crypto to", type: "count" },
		{ value: "deposit_amount_crypto_from", label: "Deposit amount crypto from", type: "count" },
		{ value: "deposit_amount_fiat_to", label: "Deposit amount fiat to", type: "count" },
		{ value: "deposit_amount_fiat_from", label: "Deposit amount fiat from", type: "count" },
		{ value: "deposit_type", label: "Deposit type", type: "select", specialType: "cryptoOrFiat" },
		{ value: "withdrawal_date", label: "Withdrawal date", type: "count" },
		{ value: "withdrawal_date_crypto", label: "Withdrawal crypto date", type: "count" },
		{ value: "withdrawal_date_fiat", label: "Withdrawal fiat date", type: "count" },
		{ value: "withdrawal_count", label: "Withdrawal count", type: "count" },
		{ value: "withdrawal_count_fiat", label: "Withdrawal fiat count", type: "count" },
		{ value: "withdrawal_count_crypto", label: "Withdrawal crypto count", type: "count" },
		{ value: "withdrawal_from", label: "Withdrawal from", type: "date" },
		{ value: "withdrawal_to", label: "Withdrawal to", type: "date" },
		{ value: "withdrawal_crypto_from", label: "Withdrawal crypto from", type: "date" },
		{ value: "withdrawal_crypto_to", label: "Withdrawal crypto to", type: "date" },
		{ value: "withdrawal_fiat_from", label: "Withdrawal fiat from", type: "date" },
		{ value: "withdrawal_fiat_to", label: "Withdrawal fiat to", type: "date" },
		{ value: "withdrawal_amount_to", label: "Withdrawal amount to", type: "count" },
		{ value: "withdrawal_amount_from", label: "Withdrawal amount from", type: "count" },
		{ value: "withdrawal_amount_crypto_to", label: "Withdrawal amount crypto to", type: "count" },
		{ value: "withdrawal_amount_crypto_from", label: "Withdrawal amount crypto from", type: "count" },
		{ value: "withdrawal_amount_fiat_to", label: "Withdrawal amount fiat to", type: "count" },
		{ value: "withdrawal_amount_fiat_from", label: "Withdrawal amount fiat from", type: "count" },
		{ value: "withdrawal_type", label: "Withdrawal type", type: "select", specialType: "cryptoOrFiat" },
		{ value: "spins", label: "Spins", type: "count" },
		{ value: "lvl_club", label: "Club", type: "count" },
		{ value: "referer", label: "Referer", type: "count" },
		{ value: "balance", label: "Balance", type: "count" },
		{ value: "registration_from", label: "Registration from", type: "date" },
		{ value: "registration_to", label: "Registration to", type: "date" },
		{ value: "registration_date", label: "Registration user days", type: "count" },
		{ value: "country", label: "Country", type: "string" },
	];

	getCohortFieldOptionsByModule = (module) => {
		const fields = this.getCohortFieldOptions();
		if (module === "deposit") return fields.filter((f) => f.value.startsWith("deposit_"));
		if (module === "withdrawal") return fields.filter((f) => f.value.startsWith("withdrawal_"));
		if (module === "registration") return fields.filter((f) => f.value.startsWith("registration_"));
		return fields.filter((f) => !f.value.startsWith("deposit_") && !f.value.startsWith("withdrawal_") && !f.value.startsWith("registration_"));
	};

	updateNewCondition = (groupId, patch) => {
		this.setState((prev) => ({
			newConditionByGroupId: {
				...(prev.newConditionByGroupId || {}),
				[groupId]: {
					...(prev.newConditionByGroupId?.[groupId] || {}),
					...patch,
				},
			},
		}));
	};

	resetNewCondition = (groupId) => {
		this.setState((prev) => ({
			newConditionByGroupId: {
				...(prev.newConditionByGroupId || {}),
				[groupId]: {},
			},
		}));
	};

	isConditionValueEmpty = (value) => value === null || value === "" || (Array.isArray(value) && value.length === 0);

	normalizeGroupConditions = (conditions, groupId) => {
		const groupConditions = (conditions || []).filter((c) => c.groupId === groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));
		if (!groupConditions.length) return conditions;
		const firstId = groupConditions[0].id;
		return (conditions || []).map((c) => {
			if (c.groupId !== groupId) return c;
			if (c.id === firstId) return { ...c, logicalOperator: null };
			if (!c.logicalOperator) return { ...c, logicalOperator: "AND" };
			return c;
		});
	};

	normalizeCohortConditions = (rawConditions) => {
		const fieldMetaByValue = new Map(this.getCohortFieldOptions().map((f) => [f.value, f]));
		const deriveModule = (field) => {
			if (!field) return "profile";
			if (field.startsWith("deposit_")) return "deposit";
			if (field.startsWith("withdrawal_")) return "withdrawal";
			if (field.startsWith("registration_")) return "registration";
			return "profile";
		};

		const conditions = Array.isArray(rawConditions) ? rawConditions.filter(Boolean) : [];
		if (!conditions.length) {
			const groupId = crypto.randomUUID();
			return {
				groups: [
					{
						groupId,
						module: "deposit",
						groupKey: "depositFilters",
						groupLogicalOperator: null,
					},
				],
				conditions: [],
			};
		}

		const hasAllGroupIds = conditions.every((c) => c && typeof c === "object" && c.groupId);
		if (!hasAllGroupIds) {
			const groupsByModule = new Map();
			const modulesOrder = [];

			conditions.forEach((c) => {
				const module = deriveModule(c.field || c.fieldLabel || "");
				if (groupsByModule.has(module)) return;
				const groupId = crypto.randomUUID();
				const groupLogicalOperator = modulesOrder.length === 0 ? null : "AND";
				groupsByModule.set(module, {
					groupId,
					module,
					groupKey: this.getGroupKeyByModule(module),
					groupLogicalOperator,
				});
				modulesOrder.push(module);
			});

			const groups = modulesOrder.map((module, idx) => {
				const g = groupsByModule.get(module);
				return {
					...g,
					groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
				};
			});

			const groupMetaByModule = new Map(groups.map((g, idx) => [g.module, { ...g, groupOrder: idx }]));
			const conditionOrderByGroupId = new Map();

			let normalizedConditions = conditions.map((c) => {
				const module = deriveModule(c.field || c.fieldLabel || "");
				const meta = groupMetaByModule.get(module);
				const prevOrder = conditionOrderByGroupId.get(meta.groupId) ?? -1;
				const conditionOrder = prevOrder + 1;
				conditionOrderByGroupId.set(meta.groupId, conditionOrder);
				const field = c.field || c.fieldLabel || "";
				const fm = fieldMetaByValue.get(field);

				return {
					...c,
					id: c.id || crypto.randomUUID(),
					field,
					fieldLabel: c.fieldLabel || fm?.label || field || "",
					conditionalType: c.conditionalType || fm?.type,
					specialType: typeof c.specialType !== "undefined" ? c.specialType : fm?.specialType,
					groupId: meta.groupId,
					module: meta.module,
					groupKey: c.groupKey || meta.groupKey,
					groupOrder: meta.groupOrder,
					groupLogicalOperator: meta.groupOrder === 0 ? null : meta.groupLogicalOperator || "AND",
					conditionOrder,
					logicalOperator: conditionOrder === 0 ? null : c.logicalOperator || "AND",
				};
			});

			groups.forEach((g) => {
				normalizedConditions = this.normalizeGroupConditions(normalizedConditions, g.groupId);
			});

			return { groups, conditions: normalizedConditions };
		}

		const groupMeta = new Map();
		const firstSeenOrder = new Map();
		conditions.forEach((c, idx) => {
			if (!c || typeof c !== "object") return;
			if (!firstSeenOrder.has(c.groupId)) firstSeenOrder.set(c.groupId, idx);
			const field = c.field || c.fieldLabel || "";
			const module = c.module || deriveModule(field);
			if (!groupMeta.has(c.groupId)) {
				groupMeta.set(c.groupId, {
					groupId: c.groupId,
					module,
					groupKey: c.groupKey || this.getGroupKeyByModule(module),
					groupLogicalOperator: c.groupLogicalOperator || null,
					groupOrder: typeof c.groupOrder === "number" ? c.groupOrder : firstSeenOrder.get(c.groupId),
				});
			}
		});

		const groups = [...groupMeta.values()]
			.sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0))
			.map((g, idx) => ({
				groupId: g.groupId,
				module: g.module || "deposit",
				groupKey: g.groupKey || this.getGroupKeyByModule(g.module || "deposit"),
				groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
			}));

		const indexByGroupId = new Map(groups.map((g, idx) => [g.groupId, idx]));
		const nextOrderByGroupId = new Map();

		let normalizedConditions = conditions
			.filter((c) => c && typeof c === "object" && indexByGroupId.has(c.groupId))
			.map((c) => {
				const groupIndex = indexByGroupId.get(c.groupId);
				const group = groups[groupIndex];
				const prevOrder = nextOrderByGroupId.get(c.groupId) ?? -1;
				const conditionOrder = typeof c.conditionOrder === "number" ? c.conditionOrder : prevOrder + 1;
				nextOrderByGroupId.set(c.groupId, Math.max(prevOrder, conditionOrder));
				const field = c.field || c.fieldLabel || "";
				const fm = fieldMetaByValue.get(field);

				return {
					...c,
					id: c.id || crypto.randomUUID(),
					field,
					fieldLabel: c.fieldLabel || fm?.label || field || "",
					conditionalType: c.conditionalType || fm?.type,
					specialType: typeof c.specialType !== "undefined" ? c.specialType : fm?.specialType,
					groupId: group.groupId,
					module: group.module,
					groupKey: c.groupKey || group.groupKey || this.getGroupKeyByModule(group.module),
					groupOrder: groupIndex,
					groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
					conditionOrder,
					logicalOperator: conditionOrder === 0 ? null : c.logicalOperator || "AND",
				};
			});

		groups.forEach((g) => {
			normalizedConditions = this.normalizeGroupConditions(normalizedConditions, g.groupId);
		});

		return { groups, conditions: normalizedConditions };
	};

	initModal = () => {
		const A = this.props.walletPromotionEditModal;
		this.props.setWalletPromotionEditModal({ ...A, loaded: false });
		const cohortRaw = this.safeJsonParse(A.cohortConditions, []);
		const normalizedCohort = this.normalizeCohortConditions(cohortRaw);
		this.setState({
			cohortGroups: normalizedCohort.groups,
			newConditionByGroupId: {},
		});
		this.props.setWalletPromotionEditModal({
			...A,
			conditions: normalizedCohort.conditions,
			loaded: true,
		});
	};

	updateCohortGroup = (groupId, patch) => {
		const nextGroups = (this.state.cohortGroups || []).map((g) => (g.groupId === groupId ? { ...g, ...patch } : g));
		this.setState({ cohortGroups: nextGroups });

		const A = this.props.walletPromotionEditModal;
		const conditions = A.conditions || [];
		const updatedConditions = conditions.map((c) =>
			c.groupId === groupId
				? {
						...c,
						...(typeof patch.module !== "undefined" ? { module: patch.module } : {}),
						...(typeof patch.module !== "undefined" ? { groupKey: this.getGroupKeyByModule(patch.module) } : {}),
						...(typeof patch.groupLogicalOperator !== "undefined" ? { groupLogicalOperator: patch.groupLogicalOperator } : {}),
					}
				: c,
		);

		this.props.setWalletPromotionEditModal({
			...A,
			conditions: updatedConditions,
		});
	};

	changeCohortGroupModule = (groupId, module) => {
		const nextGroups = (this.state.cohortGroups || []).map((g) => (g.groupId === groupId ? { ...g, module, groupKey: this.getGroupKeyByModule(module) } : g));
		this.setState({ cohortGroups: nextGroups });

		const A = this.props.walletPromotionEditModal;
		const nextConditions = (A.conditions || []).filter((c) => c.groupId !== groupId);
		this.props.setWalletPromotionEditModal({
			...A,
			conditions: nextConditions,
		});
		this.resetNewCondition(groupId);
	};

	addCohortGroup = () => {
		const groupId = crypto.randomUUID();
		this.setState((prev) => {
			const groups = [...(prev.cohortGroups || [])];
			const groupLogicalOperator = groups.length === 0 ? null : "AND";
			return {
				cohortGroups: [
					...groups,
					{
						groupId,
						module: "deposit",
						groupKey: "depositFilters",
						groupLogicalOperator,
					},
				],
			};
		});
	};

	removeCohortGroup = (groupId) => {
		const nextGroups = (this.state.cohortGroups || [])
			.filter((g) => g.groupId !== groupId)
			.map((g, idx) => ({
				...g,
				groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
			}));
		const groupIndexById = new Map(nextGroups.map((g, idx) => [g.groupId, idx]));

		const A = this.props.walletPromotionEditModal;
		const nextConditions = (A.conditions || [])
			.filter((c) => c.groupId !== groupId)
			.filter((c) => groupIndexById.has(c.groupId))
			.map((c) => {
				const groupOrder = groupIndexById.get(c.groupId);
				const group = nextGroups[groupOrder];
				return {
					...c,
					module: group.module,
					groupKey: group.groupKey || this.getGroupKeyByModule(group.module),
					groupOrder,
					groupLogicalOperator: groupOrder === 0 ? null : group.groupLogicalOperator || "AND",
				};
			});

		this.setState((prev) => {
			const nextNewConditionByGroupId = { ...(prev.newConditionByGroupId || {}) };
			delete nextNewConditionByGroupId[groupId];
			return {
				cohortGroups: nextGroups,
				newConditionByGroupId: nextNewConditionByGroupId,
			};
		});

		this.props.setWalletPromotionEditModal({
			...A,
			conditions: nextConditions,
		});
	};

	addCohortCondition = (group, groupIndex) => {
		const A = this.props.walletPromotionEditModal;
		const conditions = A.conditions || [];
		const groupConditions = conditions.filter((c) => c.groupId === group.groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));
		const newCondition = this.state.newConditionByGroupId?.[group.groupId] || {};
		if (!newCondition.field || this.isConditionValueEmpty(newCondition.value)) return;

		let value = newCondition.value;
		if (newCondition.conditionalType === "select") value = newCondition.value.value || newCondition.value;
		else if (newCondition.conditionalType === "boolean") value = newCondition.value ? 1 : 0;

		const nextOrder = groupConditions.length ? Math.max(...groupConditions.map((c) => c.conditionOrder ?? 0)) + 1 : 0;
		const logicalOperator = groupConditions.length ? newCondition.logicalOperator || "AND" : null;

		const item = {
			...newCondition,
			id: crypto.randomUUID(),
			value,
			logicalOperator,
			conditionOrder: nextOrder,
			groupId: group.groupId,
			module: group.module,
			groupKey: group.groupKey || this.getGroupKeyByModule(group.module),
			groupOrder: groupIndex,
			groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
		};

		const nextConditions = this.normalizeGroupConditions([...conditions, item], group.groupId);
		this.props.setWalletPromotionEditModal({
			...A,
			conditions: nextConditions,
		});
		this.resetNewCondition(group.groupId);
	};

	renderComponentFromType = (groupId, type, specialType) => {
		const newCondition = this.state.newConditionByGroupId?.[groupId] || {};
		if (type === "multiselect" && specialType === "riskModel") return <Multiselect selectedOptions={getRiskModelSelectedOptions(newCondition?.value)} onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOptions.map((option) => option.value) })} options={RISK_MODEL_OPTIONS} placeholder="Select risk models" selectedAriaLabel="Selected" />;
		if (type === "string") return <Input placeholder="Enter value" value={newCondition?.value || ""} onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value })} />;
		if (type === "count") return <Input placeholder="Enter value" type="number" value={newCondition?.value || ""} onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value })} />;
		if (type === "date") return <DatePicker onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.value })} value={newCondition?.value || null} placeholder="Select a date" />;
		if (type === "select" && specialType === "boolean") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={[
						{ value: "1", label: "Required" },
						{ value: "0", label: "No" },
					]}
					placeholder="Select type"
				/>
			);
		}
		if (type === "select" && specialType === "cryptoOrFiat") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={[
						{ value: "crypto", label: "Crypto" },
						{ value: "fiat", label: "Fiat" },
					]}
					placeholder="Select type"
				/>
			);
		}
		if (type === "select" && specialType === "partnerPaymentType") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={PARTNER_PAYMENT_TYPE_OPTIONS}
					placeholder="Select payment type"
				/>
			);
		}
		return null;
	};

	onDismiss = () => {
		this.props.setWalletPromotionEditModal({
			...this.props.walletPromotionEditModal,
			show: false,
			loaded: true,
			id: 0,
			linkTranslationType: "",
			link: "",
			textLink: "",
			placement: [],
			badgePercent: "",
			badgeType: { value: "none", label: "No text" },
			badgeAction: { value: "none", label: "None" },
			priority: "",
			startDate: null,
			endDate: null,
			active: { value: "1", label: "Enabled" },
			cohortConditions: "",
			conditions: [],
		});
		this.setState({ cohortGroups: [], newConditionByGroupId: {} });
	};

	editWalletPromotion = () => {
		const A = this.props.walletPromotionEditModal;
		const priority = Number(A.priority);
		const badgePercent = Number(A.badgePercent);

		if (!A.placement?.length) {
			this.props.Interface.addNoty("Error", "Placement is required");
			return;
		}
		if (Number.isNaN(priority)) {
			this.props.Interface.addNoty("Error", "Priority is required");
			return;
		}
		if (Number.isNaN(badgePercent)) {
			this.props.Interface.addNoty("Error", "Badge percent is required");
			return;
		}
		if (!A.startDate || !A.endDate) {
			this.props.Interface.addNoty("Error", "Start date and end date are required");
			return;
		}

		const badgeSettings = {
			percent: badgePercent,
			type: A.badgeType?.value || "none",
			action: A.badgeAction?.value || "none",
		};
		if (A.badgeAction?.value === "link") {
			badgeSettings.linkTranslationType = A.linkTranslationType || "";
			badgeSettings.link = A.link || "";
			badgeSettings.textLink = A.textLink || "";
		}

		const payload = {
			id: A.id,
			placement: A.placement.map((item) => item.value),
			priority,
			startDate: A.startDate,
			endDate: A.endDate,
			active: Number(A.active?.value || 0),
			cohortConditions: A.conditions || [],
			badgeSettings,
		};

		this.props.setWalletPromotionEditModal({ ...A, loaded: false });
		this.props.Interface.request("set", { class: "data", module: "walletPromotion", method: "edit" }, payload)
			.then(() => {
				this.props.Interface.addNoty("Success", "Wallet promotion was successfully edited");
				this.onDismiss();
				this.props.Interface.loadWalletPromotionList();
			})
			.catch((error) => {
				this.props.Interface.addNoty("Error", error?.text || error?.toString?.() || "Request failed");
				this.props.setWalletPromotionEditModal({ ...A, loaded: true });
			});
	};

	renderSettingsTab = () => {
		const A = this.props.walletPromotionEditModal;
		const linkTranslationTypeOptions = A.currentOption.linkTranslationType || [];
		const selectedLinkTranslationType = linkTranslationTypeOptions.find((option) => option.value === A.linkTranslationType) || null;
		return (
			<SpaceBetween size="l">
				<Container>
					<SpaceBetween size="m">
						<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
							<FormField description="Placement" label="Placement" stretch>
								<Multiselect selectedOptions={A.placement} options={A.currentOption.placementType} placeholder="Select placement" onChange={({ detail }) => this.props.setWalletPromotionEditModal({ ...A, placement: detail.selectedOptions })} />
							</FormField>
							<FormField description="Higher value means higher priority" label="Priority" stretch>
								<Input type="number" placeholder="Enter priority" value={A.priority} onChange={(event) => this.props.setWalletPromotionEditModal({ ...A, priority: event.detail.value })} />
							</FormField>
							<FormField description="Badge percent" label="Badge percent" stretch>
								<Input type="number" placeholder="Enter percent" value={A.badgePercent} onChange={(event) => this.props.setWalletPromotionEditModal({ ...A, badgePercent: event.detail.value })} />
							</FormField>
							<FormField description="Badge type" label="Badge type" stretch>
								<Select selectedOption={A.badgeType} options={A.currentOption.badgeType} placeholder="Select badge type" onChange={({ detail }) => this.props.setWalletPromotionEditModal({ ...A, badgeType: detail.selectedOption })} />
							</FormField>
							<FormField description="Badge action" label="Badge action" stretch>
								<Select selectedOption={A.badgeAction} options={A.currentOption.badgeAction} placeholder="Select badge action" onChange={({ detail }) => this.props.setWalletPromotionEditModal({ ...A, badgeAction: detail.selectedOption })} />
							</FormField>
							<FormField description="Status" label="Status" stretch>
								<Select selectedOption={A.active} options={A.currentOption.active} placeholder="Select status" onChange={({ detail }) => this.props.setWalletPromotionEditModal({ ...A, active: detail.selectedOption })} />
							</FormField>
						</Grid>
						{A.badgeAction?.value === "link" ? (
							<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 12 }]}>
								<FormField description="Translation type for link text" label="Link translation type" stretch>
									<Select
										selectedOption={selectedLinkTranslationType}
										options={linkTranslationTypeOptions}
										placeholder="Select translation type"
										onChange={({ detail }) => this.props.setWalletPromotionEditModal({ ...A, linkTranslationType: detail.selectedOption.value })}
									/>
								</FormField>
								<FormField description="Destination URL for link action" label="Link" stretch>
									<Input value={A.link} placeholder="Enter link" onChange={(event) => this.props.setWalletPromotionEditModal({ ...A, link: event.detail.value })} />
								</FormField>
								<FormField description="Link text shown to the user" label="Text link" stretch>
									<Input value={A.textLink} placeholder="Enter text link" onChange={(event) => this.props.setWalletPromotionEditModal({ ...A, textLink: event.detail.value })} />
								</FormField>
							</Grid>
						) : null}
					</SpaceBetween>
				</Container>
				<Container>
					<FormField description="Date from/to" label="Promotion date range" stretch>
						<DateRangePicker
							onChange={({ detail }) => {
								this.props.setWalletPromotionEditModal({
									...A,
									startDate: detail.value.startDate.split("+")[0],
									endDate: detail.value.endDate.split("+")[0],
								});
							}}
							value={{
								startDate: A.startDate,
								endDate: A.endDate,
								type: "absolute",
							}}
							relativeOptions={[]}
							isValidRange={(range) => {
								if (range.type === "absolute") {
									const [startDateWithoutTime] = range.startDate.split("T");
									const [endDateWithoutTime] = range.endDate.split("T");
									if (!startDateWithoutTime || !endDateWithoutTime) {
										return { valid: false, errorMessage: "The selected date range is incomplete. Select a start and end date." };
									}
									if (new Date(range.startDate) - new Date(range.endDate) > 0) {
										return { valid: false, errorMessage: "The selected date range is invalid. The start date must be before the end date." };
									}
								} else {
									return { valid: false };
								}
							}}
							i18nStrings={getDateRangeI18nStrings()}
							placeholder="Promotion date from/to"
						/>
					</FormField>
				</Container>
			</SpaceBetween>
		);
	};

	renderCohortTab = () => {
		const A = this.props.walletPromotionEditModal;
		return (
			<SpaceBetween size="l">
				<Box>
					<Button variant="primary" iconName="add-plus" onClick={this.addCohortGroup}>
						Add group
					</Button>
				</Box>
				{(this.state.cohortGroups || []).map((group, groupIndex) => {
					const moduleOptions = this.getCohortModuleOptions();
					const logicalOperatorOptions = this.getLogicalOperatorOptions();
					const selectedModule = moduleOptions.find((o) => o.value === group.module) || null;
					const groupOperatorValue = groupIndex === 0 ? "AND" : group.groupLogicalOperator || "AND";
					const selectedGroupLogicalOperator = logicalOperatorOptions.find((o) => o.value === groupOperatorValue) || null;
					const groupConditions = (A.conditions || []).filter((c) => c.groupId === group.groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));
					const newCondition = this.state.newConditionByGroupId?.[group.groupId] || {};
					const selectedConditionLogicalOperator = logicalOperatorOptions.find((o) => o.value === (newCondition.logicalOperator || "AND")) || null;
					const fieldOptions = this.getCohortFieldOptionsByModule(group.module);
					const selectedFieldOption = newCondition.field ? { value: newCondition.field, label: newCondition.fieldLabel } : null;
					const selectedOperatorOption = newCondition.operator ? { value: newCondition.operator, label: newCondition.operator } : null;
					const isAddDisabled = !newCondition.field || this.isConditionValueEmpty(newCondition.value) || (!newCondition.operator && newCondition.conditionalType !== "select" && newCondition.conditionalType !== "multiselect");

					return (
						<Container
							key={group.groupId}
							header={
								<Header
									variant="h3"
									actions={
										<Button variant="icon" iconName="delete-marker" onClick={() => this.removeCohortGroup(group.groupId)}>
											Remove group
										</Button>
									}>
									{`Group ${groupIndex + 1}`}
								</Header>
							}>
							<SpaceBetween size="m">
								<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
									<FormField label="Module" stretch>
										<Select selectedOption={selectedModule} onChange={({ detail }) => this.changeCohortGroupModule(group.groupId, detail.selectedOption.value)} options={moduleOptions} placeholder="Select module" />
									</FormField>
									<FormField label="Group operator" stretch>
										<Select
											selectedOption={selectedGroupLogicalOperator}
											onChange={({ detail }) => {
												if (groupIndex === 0) return;
												this.updateCohortGroup(group.groupId, { groupLogicalOperator: detail.selectedOption.value });
											}}
											options={logicalOperatorOptions}
											placeholder="Select operator"
											disabled={groupIndex === 0}
										/>
									</FormField>
									<Box />
								</Grid>
								<Table
									columnDefinitions={[
										{ id: "logic", header: "Logic", cell: (item) => item.logicalOperator || "-" },
										{ id: "field", header: "Field", cell: (item) => item.fieldLabel || item.field },
										{ id: "operator", header: "Operator", cell: (item) => item.operator },
										{ id: "value", header: "Value", cell: (item) => Array.isArray(item.value) ? item.value.join(", ") : item.value },
										{
											id: "actions",
											header: "Control",
											cell: (item) => (
												<Button
													variant="icon"
													iconName="delete-marker"
													onClick={() => {
														const next = this.normalizeGroupConditions(
															(A.conditions || []).filter((c) => c.id !== item.id),
															group.groupId,
														);
														this.props.setWalletPromotionEditModal({
															...A,
															conditions: next,
														});
													}}>
													Remove
												</Button>
											),
										},
									]}
									footer={
										<FormField stretch label="Add condition">
											<Grid gridDefinition={groupConditions.length ? [{ colspan: 2 }, { colspan: 4 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }] : [{ colspan: 4 }, { colspan: 3 }, { colspan: 3 }, { colspan: 2 }]}>
												{groupConditions.length > 0 && <Select selectedOption={selectedConditionLogicalOperator} placeholder="AND/OR" options={logicalOperatorOptions} onChange={({ detail }) => this.updateNewCondition(group.groupId, { logicalOperator: detail.selectedOption.value })} />}
												<Select
													selectedOption={selectedFieldOption}
													placeholder="Select field"
													options={fieldOptions}
													onChange={({ detail }) =>
														this.updateNewCondition(group.groupId, {
															field: detail.selectedOption.value,
															fieldLabel: detail.selectedOption.label,
															conditionalType: detail.selectedOption.type,
															specialType: detail.selectedOption.specialType,
															value: null,
															operator: detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect" ? "=" : null,
														})
													}
												/>
												<Select
													selectedOption={selectedOperatorOption}
													placeholder="Select operator"
													disabled={newCondition.conditionalType === "select" || newCondition.conditionalType === "multiselect"}
													options={[
														{ value: "=", label: "=" },
														{ value: ">", label: ">" },
														{ value: "<", label: "<" },
														{ value: ">=", label: ">=" },
														{ value: "<=", label: "<=" },
														{ value: "IN", label: "IN" },
													]}
													onChange={({ detail }) => this.updateNewCondition(group.groupId, { operator: detail.selectedOption.value })}
												/>
												<Box>{this.renderComponentFromType(group.groupId, newCondition.conditionalType, newCondition.specialType)}</Box>
												<Button disabled={isAddDisabled} onClick={() => this.addCohortCondition(group, groupIndex)}>
													+ Add
												</Button>
											</Grid>
										</FormField>
									}
									items={groupConditions}
									empty="No conditions set"
								/>
							</SpaceBetween>
						</Container>
					);
				})}
			</SpaceBetween>
		);
	};

	render() {
		const A = this.props.walletPromotionEditModal;
		return (
			<Modal
				onDismiss={this.onDismiss}
				visible={A.show}
				header="Edit wallet promo"
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="normal" onClick={this.onDismiss}>
								Close
							</Button>
							<Button loading={!A.loaded} variant="primary" onClick={this.editWalletPromotion}>
								Save
							</Button>
						</SpaceBetween>
					</Box>
				}>
				{!A.loaded && (
					<Box margin={{ bottom: "m" }}>
						<Spinner />
					</Box>
				)}
				<Form
					actions={<></>}
					errorText=""
					children={
						<Tabs
							tabs={[
								{ label: "Settings", id: "settings", content: this.renderSettingsTab() },
								{ label: "Cohort", id: "cohort", content: this.renderCohortTab() },
							]}
						/>
					}
				/>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const walletPromotionEditModal = useRecoilValue(walletPromotionEditModalStore);
		const setWalletPromotionEditModal = useSetRecoilState(walletPromotionEditModalStore);

		return <Component {...props} walletPromotionEditModal={walletPromotionEditModal} setWalletPromotionEditModal={setWalletPromotionEditModal} />;
	};
}

export default withRecoil(WalletPromotionEditModal);
